import { useEffect, useState } from "react";
import { PageHeader } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Eraser, FileText, Trash2, FolderOpen, Download } from "lucide-react";
import { today, fmtDate } from "@/lib/format";
import { printLetterPad, printBlankLetterhead } from "@/lib/print";
import { COMPANY } from "@/lib/company";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { ref_no: "", date: today(), to: "", subject: "", body: "", signatory: "B L Baror", designation: "Managing Director" };

function LetterPreview({ form, accent }) {
  return (
    <div className="sticky top-4" data-testid="letter-preview">
      <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Live A4 Preview</div>
      <div className="mx-auto w-full max-w-[460px] rounded-sm border border-slate-300 bg-white p-3 shadow-md" style={{ aspectRatio: "210 / 297" }}>
        <div className="flex h-full flex-col border border-slate-800 p-2.5 text-slate-900">
          <div className="flex items-start justify-between border-b-2 pb-1.5" style={{ borderColor: accent }}>
            <div className="flex items-center gap-1.5">
              <img src="/ntc-logo.jpg" alt="logo" className="h-7 w-7 rounded object-contain" />
              <div className="text-[10px] font-extrabold leading-tight" style={{ color: accent }}>{COMPANY.name}</div>
            </div>
            <div className="text-right text-[6px] leading-snug text-slate-500">
              {COMPANY.address}<br />{COMPANY.gstin} · {COMPANY.pan}<br />{COMPANY.phone} · {COMPANY.email}
            </div>
          </div>
          <div className="mb-1.5 mt-1 h-[4px] rounded-sm" style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}B3 45%, ${accent}33 100%)` }} data-testid="preview-color-band" />
          <div className="flex justify-between text-[7px]">
            <span><b>Ref No.:</b> {form.ref_no || "NTC/L-…"}</span>
            <span><b>Date:</b> {form.date ? fmtDate(form.date) : ""}</span>
          </div>
          {form.to && <div className="mt-1 whitespace-pre-line text-[7px] leading-snug"><b>To,</b><br />{form.to}</div>}
          {form.subject && <div className="mt-1.5 text-[7.5px] font-bold">Subject: <span className="underline">{form.subject}</span></div>}
          <div className="mt-1 overflow-hidden whitespace-pre-line text-[7px] leading-relaxed" data-testid="preview-body">{form.body}</div>
          <div className="mt-1 flex items-start justify-between pt-2 text-[7px]">
            <div>Thanking You,</div>
            <div className="pr-3 text-right leading-snug">
              <b>For {COMPANY.name}</b>
              <div className="h-8" />
              {form.signatory && <b>{form.signatory}<br /></b>}{form.designation && <>{form.designation}<br /></>}Authorized Signatory
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LetterPad() {
  const [form, setForm] = useState(EMPTY);
  const [letters, setLetters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [accent, setAccent] = useState(() => localStorage.getItem("ntc_letter_color") || "#334155");
  const changeAccent = (e) => {
    setAccent(e.target.value);
    localStorage.setItem("ntc_letter_color", e.target.value);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => {
    api.get("/letters").then((r) => setLetters((r.data || []).sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))))).catch(() => {});
    api.get("/letter-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveTemplate = async () => {
    const name = window.prompt("Template name (e.g. Bank Cheque Book Request):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/letter-templates", { name: name.trim(), to: form.to, subject: form.subject, body: form.body, signatory: form.signatory, designation: form.designation });
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error("Failed to save template");
    }
  };

  const removeTemplate = async (id) => {
    try {
      await api.delete(`/letter-templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const saveAndPrint = async () => {
    setSaving(true);
    try {
      const res = await api.post("/letters", form);
      toast.success(`Letter saved — Ref ${res.data.ref_no}`);
      printLetterPad(res.data, accent);
      setForm((f) => ({ ...f, ref_no: res.data.ref_no }));
      load();
    } catch (e) {
      toast.error("Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const removeLetter = async (id) => {
    try {
      await api.delete(`/letters/${id}`);
      toast.success("Letter deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };
  return (
    <div>
      <PageHeader
        title="NTC Letter Pad"
        subtitle="Type any official letter and print it on the NTC letterhead."
        action={
          <div className="flex items-center gap-2">
            <input type="color" value={accent} onChange={changeAccent} title="Letterhead header color" className="h-9 w-11 cursor-pointer rounded-md border border-border bg-card p-1" data-testid="letter-color-input" />
            <Button variant="outline" onClick={() => printBlankLetterhead(accent)} data-testid="blank-letterhead-btn">
              <FileText className="mr-2 h-4 w-4" /> Blank Letterhead
            </Button>
            <Button variant="outline" onClick={() => setForm({ ...EMPTY, date: today() })} data-testid="letter-clear-btn">
              <Eraser className="mr-2 h-4 w-4" /> Clear
            </Button>
            <Button variant="outline" onClick={saveTemplate} disabled={!form.body.trim() && !form.subject.trim()} data-testid="save-template-btn">
              <FileText className="mr-2 h-4 w-4" /> Save as Template
            </Button>
            <Button variant="outline" onClick={() => printLetterPad(form, accent)} disabled={!form.body.trim() && !form.subject.trim()} data-testid="letter-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Only
            </Button>
            <Button onClick={saveAndPrint} disabled={saving || (!form.body.trim() && !form.subject.trim())} data-testid="letter-save-print-btn">
              <Printer className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save & Print"}
            </Button>
          </div>
        }
      />
      <div className="grid items-start gap-6 xl:grid-cols-2">
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        {templates.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="letter-templates-bar">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Templates:</span>
            {templates.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-slate-50 pl-3 pr-1 text-xs font-medium">
                <button
                  type="button"
                  className="py-1 hover:text-primary"
                  title="Use this template"
                  onClick={() => setForm((f) => ({ ...f, to: t.to || "", subject: t.subject || "", body: t.body || "", signatory: t.signatory || f.signatory, designation: t.designation || f.designation }))}
                  data-testid="use-template-btn"
                >
                  {t.name}
                </button>
                <button type="button" className="rounded-full p-1 text-slate-400 hover:text-destructive" title="Delete template" onClick={() => removeTemplate(t.id)} data-testid="delete-template-btn">
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
          <Label>Ref No. (auto-generated if blank)</Label>
            <Input value={form.ref_no} onChange={set("ref_no")} placeholder="Auto: NTC/L-1001" data-testid="letter-ref" />
          </div>
          <div className="grid gap-1.5">
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={set("date")} data-testid="letter-date" />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label>To (name & address)</Label>
          <Textarea rows={3} value={form.to} onChange={set("to")} placeholder={"The Manager,\nPunjab National Bank,\nGummidipoondi Branch"} data-testid="letter-to" />
        </div>
        <div className="grid gap-1.5">
          <Label>Subject</Label>
          <Input value={form.subject} onChange={set("subject")} placeholder="Request for statement of account" data-testid="letter-subject" />
        </div>
        <div className="grid gap-1.5">
          <Label>Letter body</Label>
          <Textarea rows={12} value={form.body} onChange={set("body")} placeholder="Dear Sir/Madam,&#10;&#10;Type the letter content here…" data-testid="letter-body" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label>Signatory name</Label>
            <Input value={form.signatory} onChange={set("signatory")} data-testid="letter-signatory" />
          </div>
          <div className="grid gap-1.5">
            <Label>Signatory designation</Label>
            <Input value={form.designation} onChange={set("designation")} data-testid="letter-designation" />
          </div>
        </div>
      </div>
      <LetterPreview form={form} accent={accent} />
      </div>

      <div className="mt-6 max-w-3xl rounded-lg border border-border bg-card p-6" data-testid="saved-letters-card">
        <h2 className="mb-3 flex items-center justify-between font-display text-lg font-semibold">
          Saved Letters ({letters.length})
          {letters.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
                const lines = ["Sl No.,Ref No.,Date,To,Subject,Signatory"];
                letters.forEach((l, i) => lines.push([i + 1, l.ref_no || "", l.date || "", (l.to || "").replace(/\n/g, " "), l.subject || "", l.signatory || ""].map(cell).join(",")));
                const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ntc-letters-index-${today()}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
              data-testid="letters-excel-btn"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
            </Button>
          )}
        </h2>
        {letters.length === 0 ? (
          <p className="text-sm text-muted-foreground">No saved letters yet. Use "Save & Print" to keep a history with auto ref numbers.</p>
        ) : (
          <div className="divide-y divide-border">
            {letters.map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2 text-sm" data-testid="saved-letter-row">
                <span className="w-[110px] shrink-0 font-semibold text-primary">{l.ref_no}</span>
                <span className="w-[90px] shrink-0 text-xs text-slate-500">{l.date ? fmtDate(l.date) : "—"}</span>
                <span className="min-w-0 flex-1 truncate" title={l.subject || l.to}>{l.subject || l.to || "—"}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Load into editor" onClick={() => setForm({ ref_no: l.ref_no || "", date: l.date || today(), to: l.to || "", subject: l.subject || "", body: l.body || "", signatory: l.signatory || "", designation: l.designation || "" })} data-testid="load-letter-btn">
                  <FolderOpen className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" title="Reprint letter" onClick={() => printLetterPad(l, accent)} data-testid="reprint-letter-btn">
                  <Printer className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Delete letter" onClick={() => removeLetter(l.id)} data-testid="delete-letter-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
